/**
 * The scoped, idempotent message outbox — storage, entry shape and the
 * per-thread computed view (phase6-contract.md §3.4, DEC-093, DEC-096).
 *
 * Who owns what:
 *   - `stores/messages.store.ts` is the SINGLE owner of the reactive queue
 *     (`outbox: Map<scope, OutboxMessage[]>`), the in-memory `inFlight` set,
 *     `deliver()`, `flushOutbox()` and the `delivered` tick. This module is the
 *     storage layer + pure helpers the store calls, and {@link useMessageOutbox},
 *     the computed view a thread page reads.
 *   - An entry is persisted to `localStorage` under a scope key that names the
 *     tenant, the user and the thread:
 *
 *         syntry.msg_outbox.v2:<companyKey>:<userId>:<ThreadKey>
 *
 *     `companyKey` / `userId` come from the session identity snapshot
 *     (`services/session-scope.ts`); `ThreadKey` is the unified thread identity
 *     (`types/message.ts`). A user who signs out, or switches company, takes
 *     their queue with them ({@link clearOutboxForSession}); a draft can never
 *     surface under another identity.
 *   - The Phase 1 key (`syntry.msg_outbox.<conversationId>`, identity-less) is
 *     DELETED on first read and in every clear, never adopted (DEC-096): it may
 *     hold another user's or tenant's draft.
 *
 * Every entry carries its own `clientEventId` — the UUID v4 the server uses as
 * the idempotency key. It is minted ONCE when the entry is queued and reused on
 * every attempt, so a retry can only replay the original write (`replayed:true`)
 * or learn that it already landed (409 `idempotency_conflict`); it is never
 * re-sent under a fresh id. `requestHash` is a CLIENT change-detection hash
 * only — conflict detection is the server's 409 (DEC-093).
 */
import { computed, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { ErpatApiError } from '@erpat/sdk';
import type { SessionScope } from '@/services/session-scope';
import { useMessagesStore } from '@/stores/messages.store';
import type { ThreadKey } from '@/types/message';
import { isUuidV4, newUuidV4 } from '@/utils/uuid';

/* ── Keys ──────────────────────────────────────────────────────────────── */

/** The Phase 1 prefix. A key under it that is NOT a v2 key is a v1 key — deleted, never read. */
export const OUTBOX_V1_PREFIX = 'syntry.msg_outbox.';
/** The Phase 6 prefix: `syntry.msg_outbox.v2:<companyKey>:<userId>:<ThreadKey>`. */
export const OUTBOX_V2_PREFIX = 'syntry.msg_outbox.v2:';

/** A full v2 storage key — the "scope" every store action is keyed by. */
export type OutboxScopeKey = string;

/** Thrown by an enqueue when the session identity is not known (either field ''). */
export class OutboxScopeError extends Error {
  constructor(message = 'The message outbox needs a signed-in company and user.') {
    super(message);
    this.name = 'OutboxScopeError';
  }
}

/** Thrown by an enqueue at the `OUTBOX_MAX_ENTRIES_PER_SCOPE` bound — nothing queued is dropped. */
export class OutboxFullError extends Error {
  constructor(message = 'Too many unsent messages in this conversation.') {
    super(message);
    this.name = 'OutboxFullError';
  }
}

/** `syntry.msg_outbox.v2:<companyKey>:<userId>:` — every scope of a session starts with it; `null` without both ids. */
export function outboxSessionPrefix(session: SessionScope): string | null {
  const companyKey = session.companyKey.trim();
  const userId = session.userId.trim();
  if (!companyKey || !userId) return null;
  return `${OUTBOX_V2_PREFIX}${companyKey}:${userId}:`;
}

/** Build the scope key for a thread under a session; throws {@link OutboxScopeError} without both ids. */
export function outboxScopeKey(session: SessionScope, threadKey: ThreadKey): OutboxScopeKey {
  const prefix = outboxSessionPrefix(session);
  if (prefix === null) throw new OutboxScopeError();
  return `${prefix}${threadKey}`;
}

export function isOutboxV2Key(key: string): boolean {
  return key.startsWith(OUTBOX_V2_PREFIX);
}

export function isOutboxV1Key(key: string): boolean {
  return key.startsWith(OUTBOX_V1_PREFIX) && !isOutboxV2Key(key);
}

/** The session and thread a v2 key names, or `null` for anything else. */
export function parseOutboxScopeKey(key: string): { session: SessionScope; threadKey: ThreadKey } | null {
  if (!isOutboxV2Key(key)) return null;
  const rest = key.slice(OUTBOX_V2_PREFIX.length);
  const firstColon = rest.indexOf(':');
  if (firstColon <= 0) return null;
  const companyKey = rest.slice(0, firstColon);
  const afterCompany = rest.slice(firstColon + 1);
  const secondColon = afterCompany.indexOf(':');
  if (secondColon <= 0) return null;
  const userId = afterCompany.slice(0, secondColon);
  const threadKey = afterCompany.slice(secondColon + 1);
  if (!threadKey.startsWith('direct:') && !threadKey.startsWith('group:')) return null;
  if (threadKey.length <= threadKey.indexOf(':') + 1) return null;
  return { session: { companyKey, userId }, threadKey: threadKey as ThreadKey };
}

/** The thread a scope key names, or `null`. */
export function threadKeyOfScope(scope: OutboxScopeKey): ThreadKey | null {
  return parseOutboxScopeKey(scope)?.threadKey ?? null;
}

function storageKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) keys.push(key);
    }
  } catch {
    /* storage unavailable */
  }
  return keys;
}

function removeKeys(keys: readonly string[]): number {
  let removed = 0;
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
      removed++;
    } catch {
      /* ignore */
    }
  }
  return removed;
}

/** Delete every v1 key. Called on the first read of any scope and by every clear (DEC-096). */
export function deleteOutboxV1Keys(): number {
  return removeKeys(storageKeys().filter(isOutboxV1Key));
}

/** Every v2 scope key persisted for `session` (the keys a flush walks). */
export function listOutboxScopeKeys(session: SessionScope): OutboxScopeKey[] {
  const prefix = outboxSessionPrefix(session);
  if (prefix === null) return [];
  return storageKeys().filter((key) => key.startsWith(prefix) && parseOutboxScopeKey(key) !== null);
}

/**
 * Remove every `syntry.msg_outbox.v2:<companyKey>:<userId>:*` key AND every v1
 * key (storage only — the store clears its memory alongside). With an empty
 * snapshot (either field '') it removes EVERY key under `syntry.msg_outbox.`,
 * defensively: better to lose a draft than to leave one for the next user.
 *
 * Called from `signOutLocal()` with the session identity SNAPSHOT (§3.1) —
 * the tokens are already gone on the expiry path, so the scope must not be
 * re-read from them at clear time. A company switch is a sign-out.
 *
 * @returns the number of keys removed
 */
export function clearOutboxForSession(session: SessionScope): number {
  const scoped = outboxSessionPrefix(session);
  const doomed = storageKeys().filter((key) => {
    if (!key.startsWith(OUTBOX_V1_PREFIX)) return false;
    if (isOutboxV1Key(key)) return true;
    return scoped === null || key.startsWith(scoped);
  });
  return removeKeys(doomed);
}

/* ── Entries ───────────────────────────────────────────────────────────── */

export type OutboxStatus = 'sending' | 'failed' | 'conflict';

/**
 * Why an entry is `conflict` (terminal) or `failed` with manual retry only.
 *   `stale_attachments` — a retried entry's attachment tokens were consumed by a
 *                         first attempt the client never heard back from, and
 *                         the thread does not show the message (§3.4 "possibly
 *                         delivered", DEC-036 follow-up).
 *   `already_sent`      — the server holds this key already (409): the message
 *                         was delivered; carried on the `delivered` tick.
 *   `rejected`          — a 4xx the server will answer the same way again;
 *                         the automatic flush skips it, the user may retry.
 *   `recipient_unavailable` — the other party has LEFT the company (an inactive
 *                         or deleted staff account): the direct thread is
 *                         read-only and the server refused the message (403
 *                         `recipient_unavailable`). Terminal — never re-sent,
 *                         never "delivered"; the user may only discard it.
 */
export type OutboxReason = 'stale_attachments' | 'already_sent' | 'rejected' | 'recipient_unavailable';

/** A staged attachment on a queued message (the token survives a reload for retry). */
export interface OutboxAttachment {
  token: string;
  name: string;
  kind: 'image' | 'file';
}

export interface OutboxMessage {
  /** Client-only id (never a server id). The render key + retry handle. */
  localId: string;
  /** The idempotency key (lower-case UUID v4) — minted once, reused on every attempt. */
  clientEventId: string;
  /** Plain-text body the user typed ('' for an attachment-only message). */
  body: string;
  /** Staged attachment tokens/metadata sent with the message. */
  attachments: OutboxAttachment[];
  /** sha256 hex (Web Crypto; FNV-1a fallback) over `ThreadKey\nnormalizedBody\ntokens.join(',')`. */
  requestHash: string;
  status: OutboxStatus;
  reason?: OutboxReason;
  /** Send attempts so far — incremented and persisted BEFORE each request goes out. */
  attempts: number;
  /** ms — when it was first queued. */
  createdAt: number;
}

const ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

/**
 * The text form two copies of one message share: tags stripped (a delivered
 * message comes back as sanitized HTML), common entities decoded, whitespace
 * collapsed, trimmed. Case is kept — "ok" and "OK" are different messages.
 */
export function normalizeMessageText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:nbsp|amp|lt|gt|quot|#39|apos);/g, (entity) => ENTITY_MAP[entity] ?? entity)
    .replace(/&#(\d+);/g, (_, code: string) => {
      const point = Number(code);
      return Number.isFinite(point) && point > 0 && point < 0x110000 ? String.fromCodePoint(point) : '';
    })
    .replace(/\s+/g, ' ')
    .trim();
}

/** 64-bit FNV-1a as 16 hex chars — the hash when Web Crypto is unavailable (insecure context). */
export function fnv1a64Hex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, '0');
}

/** The material the request hash covers (exported so a spec can pin it). */
export function requestHashMaterial(threadKey: ThreadKey, body: string, tokens: readonly string[]): string {
  return `${threadKey}\n${normalizeMessageText(body)}\n${tokens.join(',')}`;
}

/**
 * The client change-detection hash (DEC-093): sha256 hex over
 * {@link requestHashMaterial} through Web Crypto, FNV-1a when `crypto.subtle`
 * is absent. It is stored on the entry and never sent.
 */
export async function computeRequestHash(threadKey: ThreadKey, body: string, tokens: readonly string[]): Promise<string> {
  const material = requestHashMaterial(threadKey, body, tokens);
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (subtle && typeof subtle.digest === 'function') {
    try {
      const digest = await subtle.digest('SHA-256', new TextEncoder().encode(material));
      return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      /* fall through to FNV-1a */
    }
  }
  return fnv1a64Hex(material);
}

/** Mint a new queued entry (status `sending`, no attempts yet). */
export function createOutboxEntry(input: {
  body: string;
  attachments: OutboxAttachment[];
  requestHash: string;
  clientEventId?: string;
}): OutboxMessage {
  const clientEventId = input.clientEventId && isUuidV4(input.clientEventId) ? input.clientEventId.toLowerCase() : newUuidV4();
  return {
    localId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    clientEventId,
    body: input.body,
    attachments: input.attachments.map((a) => ({ token: a.token, name: a.name, kind: a.kind })),
    requestHash: input.requestHash,
    status: 'sending',
    attempts: 0,
    createdAt: Date.now(),
  };
}

function isReason(value: unknown): value is OutboxReason {
  return (
    value === 'stale_attachments' ||
    value === 'already_sent' ||
    value === 'rejected' ||
    value === 'recipient_unavailable'
  );
}

function normalizeStored(raw: unknown): OutboxMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.localId !== 'string' || typeof rec.body !== 'string') return null;
  // A v2 entry always persisted its key before the first attempt; one without a
  // valid key is corrupt, and minting a fresh key could deliver it twice.
  if (!isUuidV4(rec.clientEventId)) return null;
  const attachments: OutboxAttachment[] = Array.isArray(rec.attachments)
    ? rec.attachments
        .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object' && typeof (a as Record<string, unknown>).token === 'string')
        .map((a) => ({
          token: String(a.token),
          name: String(a.name ?? ''),
          kind: a.kind === 'image' ? 'image' : 'file',
        }))
    : [];
  const reason = isReason(rec.reason) ? rec.reason : undefined;
  // Only `sending` hydrates as `failed` (nothing is in flight after a reload);
  // `failed` and `conflict` are preserved together with their reason.
  const status: OutboxStatus = rec.status === 'conflict' ? 'conflict' : 'failed';
  const attempts = Number(rec.attempts);
  const createdAt = Number(rec.createdAt);
  const entry: OutboxMessage = {
    localId: rec.localId,
    clientEventId: rec.clientEventId.toLowerCase(),
    body: rec.body,
    attachments,
    requestHash: typeof rec.requestHash === 'string' ? rec.requestHash : '',
    status,
    attempts: Number.isFinite(attempts) && attempts > 0 ? Math.trunc(attempts) : 0,
    createdAt: Number.isFinite(createdAt) && createdAt > 0 ? createdAt : Date.now(),
  };
  if (reason && (status === 'conflict' || status === 'failed')) entry.reason = reason;
  return entry;
}

/**
 * Read one scope from storage. Anything that was `sending` comes back as
 * `failed` (retryable); `failed` keeps its reason; `conflict` is preserved with
 * its reason (terminal until the user discards or edits it). Every v1 key is
 * deleted on the way (DEC-096).
 */
export function loadOutbox(scope: OutboxScopeKey): OutboxMessage[] {
  deleteOutboxV1Keys();
  try {
    const raw = localStorage.getItem(scope);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeStored).filter((m): m is OutboxMessage => m !== null);
  } catch {
    return [];
  }
}

/** Write one scope to storage; an empty queue removes the key. */
export function persistOutbox(scope: OutboxScopeKey, entries: readonly OutboxMessage[]): void {
  try {
    if (entries.length) localStorage.setItem(scope, JSON.stringify(entries));
    else localStorage.removeItem(scope);
  } catch {
    /* private mode / quota — non-fatal; the reactive queue still holds it */
  }
}

/* ── Error classification (branch on code/status, never on message text) ── */

/** The server's refusal of a direct message to a colleague who has left the company. */
export const RECIPIENT_UNAVAILABLE = 'recipient_unavailable';

/**
 * The direct thread is read-only: its other party has left the company, and the
 * server refused the message (403 `recipient_unavailable` — never persisted).
 * Classified by CODE, whatever the status, so a server that ever answered it
 * with a 409 still cannot be mistaken for "already delivered".
 */
export function isRecipientUnavailableError(err: unknown): boolean {
  return err instanceof ErpatApiError && err.code === RECIPIENT_UNAVAILABLE;
}

/**
 * A 409 `idempotency_conflict`: the key IS persisted, i.e. the message was
 * delivered (DEC-093). A `recipient_unavailable` refusal is never "delivered",
 * whatever status it arrives with.
 */
export function isAlreadySentError(err: unknown): boolean {
  if (isRecipientUnavailableError(err)) return false;
  return err instanceof ErpatApiError && (err.code === 'idempotency_conflict' || err.status === 409);
}

/**
 * A 422 that names `attachments` or one of the entry's tokens — the server
 * validates staged tokens BEFORE the idempotency lookup and consumes them on
 * the first success (DEC-036), so on a retry this is the "possibly delivered"
 * signal (§3.4).
 */
export function isStaleAttachmentsError(err: unknown, tokens: readonly string[]): boolean {
  if (!(err instanceof ErpatApiError) || err.status !== 422) return false;
  const details = err.details;
  const fields = details && typeof details.fields === 'object' && details.fields !== null
    ? (details.fields as Record<string, unknown>)
    : null;
  if (fields) {
    if ('attachments' in fields) return true;
    if (tokens.some((token) => token in fields)) return true;
  }
  return details !== undefined && 'attachments' in details;
}

/** A server-side refusal the automatic flush must not repeat (manual retry only). */
export function isRejectedError(err: unknown): boolean {
  if (!(err instanceof ErpatApiError)) return false;
  if (err.code === 'invalid_client_event_id') return true;
  if (err.status === 0 || err.status === 408 || err.status === 429) return false;
  return err.status >= 400 && err.status < 500;
}

/* ── The page's view over the store ────────────────────────────────────── */

export interface UseMessageOutbox {
  /** The scope's queue, oldest first (0 or 1 entry under the composer guard). */
  messages: ComputedRef<OutboxMessage[]>;
  /** True while any entry exists (sending, failed OR conflict) — blocks a new send. */
  hasPending: ComputedRef<boolean>;
  /** True while an entry is actively in flight. */
  isSending: ComputedRef<boolean>;
  /** True while a terminal `conflict` entry waits for Discard / Edit & resend. */
  hasConflict: ComputedRef<boolean>;
}

/**
 * A computed view over the store's queue for ONE scope (§3.4 "ownership").
 * `scope` may be a getter/ref: a direct thread opened by a reply id adopts the
 * root id once the thread answers, and the view follows. A `null` scope (the
 * session identity not known yet) reads as empty. The store hydrates the scope
 * from storage the first time it is named here.
 */
export function useMessageOutbox(scope: MaybeRefOrGetter<OutboxScopeKey | null>): UseMessageOutbox {
  const store = useMessagesStore();
  watch(
    () => toValue(scope),
    (key) => {
      if (key) store.hydrateOutbox(key);
    },
    { immediate: true },
  );
  const messages = computed<OutboxMessage[]>(() => {
    const key = toValue(scope);
    return key ? store.outboxEntries(key) : [];
  });
  return {
    messages,
    hasPending: computed(() => messages.value.length > 0),
    isSending: computed(() => messages.value.some((m) => m.status === 'sending')),
    hasConflict: computed(() => messages.value.some((m) => m.status === 'conflict')),
  };
}
